//#region ../src/utils/format-distance.ts
/**
* Clamps a decimals option into `toFixed`'s domain.
*
* `toFixed` throws a `RangeError` for fraction digits below 0 or above 100,
* and this module's contract is "total; never throws" — so an out-of-range
* option clamps rather than propagating the throw into a render loop. (`NaN`
* survives the clamp, and `toFixed` itself treats it as 0.)
*/
function clampDecimals(decimals) {
	return Math.min(100, Math.max(0, decimals));
}
/**
* Rounds `value` to the nearest multiple of `step`.
*
* `> 0` and not just `!== 1`: a zero step would divide by zero and a negative
* one would flip the sign — both leave the value unstepped instead. A
* FRACTIONAL step is honoured; the option doc promises "this multiple".
*/
function stepMetres(value, step) {
	return step > 0 && step !== 1 ? Math.round(value / step) * step : value;
}
/**
* Formats a distance in metres for display.
*
* **Negative input clamps to zero.** Distance is a magnitude; a negative one
* means a caller subtracted in the wrong order, and "-3.0 m" on screen is
* strictly less useful than "0 m" for finding that out. Non-finite input
* likewise formats as zero rather than propagating `NaN` into the UI.
*/
function formatDistance(metres, options = {}) {
	const { metreStep = 1, metreDecimals = 1, kmDecimals = 1, kilometreAboveM = 1e3 } = options;
	const safe = Number.isFinite(metres) ? Math.max(0, metres) : 0;
	if (kilometreAboveM !== null && safe >= kilometreAboveM) return `${(safe / 1e3).toFixed(clampDecimals(kmDecimals))} km`;
	return `${stepMetres(safe, metreStep).toFixed(clampDecimals(metreDecimals))} m`;
}
//#endregion
export { formatDistance };
